import React, { lazy, Suspense, useMemo, useContext } from 'react';
import { FaIcon } from '../../../common/Icons.jsx';
import styles from './FlipCardWidget.module.scss';
import SkeletonGrid from '../../../../skeleton_loaders/common/SkeletonGrid.jsx';
import { rgbFromHex } from '../../../../helpers/parsers.js';
import LexicalEditor from '../../../common/richTextPlugins/LexicalEditor.lazy.jsx';
import withSuspense from '../../../../helpers/withSuspense.jsx';
import { useSelector, ReactReduxContext } from 'react-redux';
import ImageAttachment from '../ImageAttachment.jsx';

const InlineSVG = withSuspense(lazy(() => import('../../../common/InlineSVG.jsx')), <SkeletonGrid />);

function FlipCardContent({widgetId, theme, flip, side = 'front', widget}) {
  const context = useContext(ReactReduxContext);
  const hasProvider = !!context;
  const flip_behavior = (hasProvider
    ? useSelector(state => state.dashboard.widgets[widgetId].settings?.flip_behavior)
    : widget.settings?.flip_behavior
  ) || 'click';
  // Gen1 dashboard uses same render method, temporary fix
  const content = hasProvider
    ? useSelector(state => state.dashboard?.widgets?.[widgetId]?.settings?.[side]) ?? widget.settings?.[side] ?? {}
    : widget.settings?.[side] ?? {};


  const editorStyle = useMemo(() => ({
    '--editor-text-size': 'clamp(1rem, 10cqw, 1em)',
    '--editor-text-color': content['text_color'],
  }), [content]);

  const Icon = useMemo(() => {
    if (content['icon_type']) {
      return <FaIcon type={content['icon_type']} style={{color: content['icon_color']}} className={styles['fa']} />
    }
    if (content.attachment?.path) {
      const path = content.attachment.path;
      return <ImageAttachment width='100%' path={path} />
    }
    if (content['image_url'] && !hasProvider) { // gen1 render
      return <InlineSVG url={content['image_url']} style={{width: '100%', height: 'auto'}} responsive={true} />
    }
    return null;
  }, [content['image_url'], content['attachment'], content['icon_type']]);

  const rgb = useMemo(() => {
    const [r, g, b] = rgbFromHex(content['card_color'] || '#ffffff');
    return `${r}, ${g}, ${b}`;
  }, [content]);
  
  const isInsideActions = (elem) => !!elem?.closest?.('.widget-actions');

  const handleMouseEnter = (e) => {
    const inside = isInsideActions(e.relatedTarget);
    if (!inside && flip_behavior === 'hover' && side === 'front'){
      flip();
    }
  };

  const handleMouseLeave = (e) => {
    const inside = isInsideActions(e.relatedTarget);
    if (!inside && flip_behavior == 'hover' && side === 'back'){
      flip();
    }
  }

  return (
    <div onClick={flip_behavior === 'click' ? flip : undefined} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
      backgroundColor: content['card_color'],
      '--widget-background-rgb': rgb,
    }} className={`${styles[`flip-card-${side}`]} flip-card-${side} thin-scrollbar`}>
      <div className={`${styles['fade-overlay']} ${styles['top']} fade-overlay top`}></div>
      <FaIcon type='sync' className={`${styles['flip-card-icon']} flip-card-icon`}/>
      {Icon && <div className={`${styles['icon-wrapper']} icon-wrapper`}>
        <Suspense fallback={<SkeletonGrid />}>
          {Icon}
        </Suspense>
      </div>}
      <LexicalEditor key={`${widgetId}-${side}-${content['content']}`} value={content['content']} readOnly={true} preview={true} transparentBackground={true} editorStyle={editorStyle} />
      <div className={`${styles['fade-overlay']} ${styles['bottom']} fade-overlay bottom`}></div>
    </div>
  )
}

/**
 * TODO: Export as image has problems when widget header is visible on flip card widget (so when title visible).
 * Likely has to do with back side absolute position / z indexes.
 * 
 * When displaying back of card, front is immediately shown on view before turning. May be polished (FlipCardWidget.module.scss)
 */
function FlipCardWidget({ widgetId, theme, widgetRef, widget = {} }) {
  const flip = () => {
    if (widgetRef.current) {
      widgetRef.current.classList.toggle(styles['flipped']);
      widgetRef.current.classList.toggle('flipped'); // gen1
    }
  }
  
  const Content = useMemo(() => {
    return <>{['front', 'back'].map(side => (
        <FlipCardContent key={side} flip={flip} widgetId={widgetId} theme={theme} side={side} widget={widget} />
      ))}
    </>
  }, [widgetId]);

  return (
    <>
      {Content}
    </>
  )
}

export default FlipCardWidget;